
import React, { useState, useEffect } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Trophy, UserCheck, ClipboardList, Users } from "lucide-react";
import { toast } from "sonner";

interface Team {
  id: string;
  player1: string;
  player2: string;
  points: number;
  gamesPlayed: number;
  wins: number;
  losses: number;
}

interface Match {
  court: number;
  team1: Team;
  team2: Team;
  score?: { team1: number; team2: number };
}

interface TeamAmericanoTournamentProps {
  participants: any[];
  onBack?: () => void;
}

export const TeamAmericanoTournament: React.FC<TeamAmericanoTournamentProps> = ({ participants, onBack }) => {
  const { t, language } = useLanguage();
  const [teams, setTeams] = useState<Team[]>([]);
  const [currentRound, setCurrentRound] = useState(1);
  const [matches, setMatches] = useState<Match[]>([]);
  const [isRoundActive, setIsRoundActive] = useState(false);
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const [tournamentStarted, setTournamentStarted] = useState(false);
  const [showTeamCreation, setShowTeamCreation] = useState(false);
  const [availablePlayers, setAvailablePlayers] = useState(participants);
  const [currentTeams, setCurrentTeams] = useState<{player1: any | null, player2: any | null}[]>([]);

  // Start manual team creation
  const startTeamCreation = () => {
    if (participants.length % 2 !== 0) {
      toast.error(
        language === 'no' 
          ? `Trenger et partall spillere (${participants.length} valgt)`
          : `Need an even number of players (${participants.length} selected)`
      );
      return;
    }

    const numTeams = Math.floor(participants.length / 2);
    const initialTeams = Array(numTeams).fill(null).map(() => ({ player1: null, player2: null }));
    
    setCurrentTeams(initialTeams);
    setAvailablePlayers([...participants]);
    setShowTeamCreation(true);
  };

  // Add player to team
  const addPlayerToTeam = (teamIndex: number, position: 'player1' | 'player2', player: any) => {
    const updatedTeams = [...currentTeams];
    updatedTeams[teamIndex] = {
      ...updatedTeams[teamIndex],
      [position]: player
    };
    setCurrentTeams(updatedTeams);

    // Remove player from available players
    setAvailablePlayers(availablePlayers.filter(p => p !== player));
  };

  // Remove player from team
  const removePlayerFromTeam = (teamIndex: number, position: 'player1' | 'player2') => {
    const updatedTeams = [...currentTeams];
    const playerToRemove = updatedTeams[teamIndex][position];
    updatedTeams[teamIndex] = {
      ...updatedTeams[teamIndex],
      [position]: null
    };
    setCurrentTeams(updatedTeams);

    // Add player back to available players
    if (playerToRemove) {
      setAvailablePlayers([...availablePlayers, playerToRemove]);
    }
  };

  // Finalize teams and start tournament
  const finalizeTeams = () => {
    // Check if all teams are complete
    const incompleteTeams = currentTeams.filter(team => !team.player1 || !team.player2);
    if (incompleteTeams.length > 0) {
      toast.error(
        language === 'no' 
          ? 'Alle lag må ha 2 spillere'
          : 'All teams must have 2 players'
      );
      return;
    }

    // Create final teams
    const finalTeams: Team[] = currentTeams.map((team, index) => ({
      id: `team-${index + 1}`,
      player1: `${team.player1.fornavn} ${team.player1.etternavn.charAt(0)}.`,
      player2: `${team.player2.fornavn} ${team.player2.etternavn.charAt(0)}.`,
      points: 0,
      gamesPlayed: 0,
      wins: 0,
      losses: 0
    }));

    setTeams(finalTeams);
    setTournamentStarted(true);
    setShowTeamCreation(false);
    
    // Automatically start the first round
    const firstRoundMatches = generateMatches(finalTeams, 1);
    setMatches(firstRoundMatches);
    setIsRoundActive(true);
    
    toast.success(
      language === 'no' 
        ? `${finalTeams.length} lag opprettet og runde 1 startet!`
        : `${finalTeams.length} teams created and round 1 started!`
    );
  };

  // Generate round-robin matches
  const generateMatches = (teamList: Team[], round: number) => {
    const newMatches: Match[] = [];
    const teamsCount = teamList.length;
    
    // Round-robin algorithm for even number of teams
    for (let i = 0; i < teamsCount; i++) {
      for (let j = i + 1; j < teamsCount; j++) {
        const roundForMatch = ((i * (teamsCount - 1) + j - 1) % (teamsCount - 1)) + 1;
        
        if (roundForMatch === round) {
          newMatches.push({
            court: Math.floor(newMatches.length / 1) + 1,
            team1: teamList[i],
            team2: teamList[j]
          });
        }
      }
    }
    
    return newMatches;
  };

  // Alternative: Generate matches by ranking (better teams play each other)
  const generateRankedMatches = (teamList: Team[]) => {
    const sortedTeams = [...teamList].sort((a, b) => {
      if (b.points !== a.points) return b.points - a.points;
      return b.wins - a.wins;
    });

    const newMatches: Match[] = [];
    for (let i = 0; i < sortedTeams.length; i += 2) {
      if (sortedTeams[i + 1]) {
        newMatches.push({
          court: Math.floor(i / 2) + 1,
          team1: sortedTeams[i],
          team2: sortedTeams[i + 1]
        });
      }
    }

    return newMatches;
  };

  const startRound = () => {
    // Use ranked matches after first round for more competitive play
    const roundMatches = currentRound === 1 
      ? generateMatches(teams, 1)
      : generateRankedMatches(teams);
      
    setMatches(roundMatches);
    setIsRoundActive(true);
  };

  const submitScore = (matchIndex: number, team1Score: number, team2Score: number) => {
    const match = matches[matchIndex];
    const updatedMatches = [...matches];
    updatedMatches[matchIndex] = {
      ...match,
      score: { team1: team1Score, team2: team2Score }
    };
    setMatches(updatedMatches);

    // Update team points and stats
    const updatedTeams = teams.map(team => {
      if (team.id === match.team1.id) {
        const won = team1Score > team2Score;
        return {
          ...team,
          points: team.points + (won ? 3 : 1),
          gamesPlayed: team.gamesPlayed + 1,
          wins: team.wins + (won ? 1 : 0),
          losses: team.losses + (won ? 0 : 1)
        };
      } else if (team.id === match.team2.id) {
        const won = team2Score > team1Score;
        return {
          ...team,
          points: team.points + (won ? 3 : 1),
          gamesPlayed: team.gamesPlayed + 1,
          wins: team.wins + (won ? 1 : 0),
          losses: team.losses + (won ? 0 : 1)
        };
      }
      return team;
    });

    setTeams(updatedTeams);
  };

  const finishRound = () => {
    const nextRound = currentRound + 1;
    setCurrentRound(nextRound);
    
    // Automatically start the next round with ranked matches
    const nextRoundMatches = generateRankedMatches(teams);
    setMatches(nextRoundMatches);
    // Keep isRoundActive true to continue with the next round
    
    toast.success(
      language === 'no' 
        ? `Runde ${currentRound} fullført! Runde ${nextRound} startet.`
        : `Round ${currentRound} completed! Round ${nextRound} started.`
    );
  };

  // Get leaderboard
  const leaderboard = [...teams].sort((a, b) => {
    if (b.points !== a.points) return b.points - a.points;
    if (b.wins !== a.wins) return b.wins - a.wins;
    return a.losses - b.losses;
  });

  // Show team creation interface if tournament hasn't started
  if (!tournamentStarted) {
    // Show manual team creation interface
    if (showTeamCreation) {
      return (
        <div className="mt-8 border-t pt-6 space-y-6">
          <div className="flex items-center gap-4">
            {onBack && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowTeamCreation(false)}
                className="text-gray-600 hover:text-gray-900"
              >
                ← {language === 'no' ? 'Tilbake' : 'Back'}
              </Button>
            )}
            <h2 className="font-bold text-2xl flex items-center gap-2">
              <Users className="text-purple-500" />
              {language === 'no' ? 'Opprett Lag Manuelt' : 'Create Teams Manually'}
            </h2>
          </div>

          <div className="grid gap-6">
            {/* Available Players */}
            <Card>
              <CardHeader>
                <CardTitle>
                  {language === 'no' ? 'Tilgjengelige Spillere' : 'Available Players'} ({availablePlayers.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                  {availablePlayers.map((player, index) => (
                    <div
                      key={index}
                      className="p-2 bg-gray-100 rounded text-sm cursor-move hover:bg-gray-200"
                      draggable
                    >
                      {player.fornavn} {player.etternavn}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Teams Creation */}
            <div className="grid gap-4">
              {currentTeams.map((team, teamIndex) => (
                <Card key={teamIndex}>
                  <CardHeader>
                    <CardTitle className="text-lg">
                      {language === 'no' ? `Lag ${teamIndex + 1}` : `Team ${teamIndex + 1}`}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Player 1 */}
                      <div className="space-y-2">
                        <label className="text-sm font-medium">
                          {language === 'no' ? 'Spiller 1' : 'Player 1'}
                        </label>
                        {team.player1 ? (
                          <div className="flex items-center justify-between p-2 bg-purple-100 rounded">
                            <span className="text-sm">
                              {team.player1.fornavn} {team.player1.etternavn}
                            </span>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => removePlayerFromTeam(teamIndex, 'player1')}
                            >
                              ×
                            </Button>
                          </div>
                        ) : (
                          <div className="border-2 border-dashed border-gray-300 rounded p-4">
                            <select
                              className="w-full p-2 border rounded"
                              onChange={(e) => {
                                const playerIndex = parseInt(e.target.value);
                                if (!isNaN(playerIndex)) {
                                  addPlayerToTeam(teamIndex, 'player1', availablePlayers[playerIndex]);
                                }
                              }}
                              value=""
                            >
                              <option value="">
                                {language === 'no' ? 'Velg spiller...' : 'Select player...'}
                              </option>
                              {availablePlayers.map((player, index) => (
                                <option key={index} value={index}>
                                  {player.fornavn} {player.etternavn}
                                </option>
                              ))}
                            </select>
                          </div>
                        )}
                      </div>

                      {/* Player 2 */}
                      <div className="space-y-2">
                        <label className="text-sm font-medium">
                          {language === 'no' ? 'Spiller 2' : 'Player 2'}
                        </label>
                        {team.player2 ? (
                          <div className="flex items-center justify-between p-2 bg-purple-100 rounded">
                            <span className="text-sm">
                              {team.player2.fornavn} {team.player2.etternavn}
                            </span>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => removePlayerFromTeam(teamIndex, 'player2')}
                            >
                              ×
                            </Button>
                          </div>
                        ) : (
                          <div className="border-2 border-dashed border-gray-300 rounded p-4">
                            <select
                              className="w-full p-2 border rounded"
                              onChange={(e) => {
                                const playerIndex = parseInt(e.target.value);
                                if (!isNaN(playerIndex)) {
                                  addPlayerToTeam(teamIndex, 'player2', availablePlayers[playerIndex]);
                                }
                              }}
                              value=""
                            >
                              <option value="">
                                {language === 'no' ? 'Velg spiller...' : 'Select player...'}
                              </option>
                              {availablePlayers.map((player, index) => (
                                <option key={index} value={index}>
                                  {player.fornavn} {player.etternavn}
                                </option>
                              ))}
                            </select>
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Finalize Button */}
            <Card>
              <CardContent className="p-6 text-center">
                <Button 
                  onClick={finalizeTeams}
                  className="bg-purple-600 hover:bg-purple-700"
                  disabled={currentTeams.some(team => !team.player1 || !team.player2)}
                >
                  <Users className="mr-2 h-4 w-4" />
                  {language === 'no' ? 'Start Turnering' : 'Start Tournament'}
                </Button>
                {currentTeams.some(team => !team.player1 || !team.player2) && (
                  <p className="text-sm text-red-600 mt-2">
                    {language === 'no' 
                      ? 'Alle lag må ha 2 spillere før turneringen kan starte'
                      : 'All teams must have 2 players before tournament can start'
                    }
                  </p>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      );
    }

    return (
      <div className="mt-8 border-t pt-6 space-y-6">
        <div className="flex items-center gap-4">
          {onBack && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onBack}
              className="text-gray-600 hover:text-gray-900"
            >
              ← {language === 'no' ? 'Tilbake' : 'Back'}
            </Button>
          )}
          <h2 className="font-bold text-2xl flex items-center gap-2">
            <Users className="text-purple-500" />
            {language === 'no' ? 'Opprett Team Americano' : 'Team Americano'}
          </h2>
        </div>

        <Card>
          <CardContent className="p-6 text-center space-y-4">
            <div className="space-y-2">
              <h3 className="text-lg font-semibold">
                {language === 'no' ? 'Opprett Lag' : 'Create Teams'}
              </h3>
              <p className="text-muted-foreground">
                {language === 'no' 
                  ? `Vil lage ${Math.floor(participants.length / 2)} lag fra ${participants.length} spillere`
                  : `Will create ${Math.floor(participants.length / 2)} teams from ${participants.length} players`
                }
              </p>
            </div>
            <Button 
              onClick={startTeamCreation} 
              className="bg-purple-600 hover:bg-purple-700"
              disabled={participants.length < 4 || participants.length % 2 !== 0}
            >
              <Users className="mr-2 h-4 w-4" />
              {language === 'no' ? 'Opprett Lag' : 'Create Teams'}
            </Button>
            {(participants.length < 4 || participants.length % 2 !== 0) && (
              <p className="text-sm text-red-600">
                {language === 'no' 
                  ? 'Trenger minimum 4 spillere og et partall'
                  : 'Need minimum 4 players and an even number'
                }
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="mt-8 border-t pt-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-4">
          {onBack && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onBack}
              className="text-gray-600 hover:text-gray-900"
            >
              ← {language === 'no' ? 'Tilbake' : 'Back'}
            </Button>
          )}
          <h2 className="font-bold text-2xl flex items-center gap-2">
            <Trophy className="text-purple-500" />
            {language === 'no' ? `Team Americano Runde ${currentRound}` : `Team Americano Round ${currentRound}`}
          </h2>
        </div>

        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowLeaderboard(!showLeaderboard)}
          >
            <ClipboardList className="mr-2 h-4 w-4" />
            {showLeaderboard 
              ? (language === 'no' ? 'Skjul resultater' : 'Hide results')
              : (language === 'no' ? 'Vis resultater' : 'Show results')}
          </Button>
        </div>
      </div>

      {/* Matches */}
      {isRoundActive && !showLeaderboard && (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold">
            {language === 'no' ? `Runde ${currentRound}` : `Round ${currentRound}`}
          </h3>
          
          <div className="grid gap-4">
            {matches.map((match, index) => (
              <Card key={index}>
                <CardContent className="p-4">
                  <div className="flex justify-between items-center">
                    <div>
                      <h4 className="font-semibold mb-2">
                        {language === 'no' ? `Bane ${match.court}` : `Court ${match.court}`}
                      </h4>
                      <div className="text-sm">
                        <div className="font-medium">
                          {language === 'no' ? 'Lag' : 'Team'} 1: {match.team1.player1} & {match.team1.player2}
                        </div>
                        <div className="text-gray-500 my-1">{language === 'no' ? 'mot' : 'vs'}</div>
                        <div className="font-medium">
                          {language === 'no' ? 'Lag' : 'Team'} 2: {match.team2.player1} & {match.team2.player2}
                        </div>
                      </div>
                    </div>

                    <div className="text-right">
                      {!match.score ? (
                        <div className="space-y-2">
                          <div className="flex gap-2">
                            <Button
                              onClick={() => submitScore(index, 6, 4)}
                              size="sm"
                              variant="outline"
                            >
                              6-4
                            </Button>
                            <Button
                              onClick={() => submitScore(index, 4, 6)}
                              size="sm"
                              variant="outline"
                            >
                              4-6
                            </Button>
                          </div>
                          <div className="flex gap-2">
                            <Button
                              onClick={() => submitScore(index, 6, 3)}
                              size="sm"
                              variant="outline"
                            >
                              6-3
                            </Button>
                            <Button
                              onClick={() => submitScore(index, 3, 6)}
                              size="sm"
                              variant="outline"
                            >
                              3-6
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div className="text-lg font-bold text-purple-600">
                          {match.score.team1} - {match.score.team2}
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {matches.every(m => m.score) && (
            <Card>
              <CardContent className="p-6 text-center">
                <Button onClick={finishRound} className="bg-purple-600 hover:bg-purple-700">
                  {language === 'no' ? 'Fullfør Runde' : 'Finish Round'}
                </Button>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Leaderboard */}
      {showLeaderboard && (
        <Card>
          <CardHeader>
            <CardTitle>{language === 'no' ? 'Resultater' : 'Leaderboard'}</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{language === 'no' ? 'Plass' : 'Position'}</TableHead>
                  <TableHead>{language === 'no' ? 'Lag' : 'Team'}</TableHead>
                  <TableHead className="text-center">{language === 'no' ? 'Poeng' : 'Points'}</TableHead>
                  <TableHead className="text-center">{language === 'no' ? 'Kamper' : 'Matches'}</TableHead>
                  <TableHead className="text-center">{language === 'no' ? 'Seire' : 'Wins'}</TableHead>
                  <TableHead className="text-center">{language === 'no' ? 'Tap' : 'Losses'}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {leaderboard.map((team, index) => (
                  <TableRow key={team.id}>
                    <TableCell className="font-semibold">{index + 1}</TableCell>
                    <TableCell>{team.player1} & {team.player2}</TableCell>
                    <TableCell className="text-center">{team.points}</TableCell>
                    <TableCell className="text-center">{team.gamesPlayed}</TableCell>
                    <TableCell className="text-center">{team.wins}</TableCell>
                    <TableCell className="text-center">{team.losses}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
